import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import type { TopBarProps } from "./TopBar";
import type { SectionNavId, SectionNavItem } from "./SectionNav";
import { CommandPalette, useCommandPalette } from "../features/command/CommandPalette";
import type { CommandLineTarget } from "../features/command/CommandPalette";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationPanel } from "../features/notifications/NotificationPanel";
import { useNotifications } from "../features/notifications/useNotifications";
import { useDesktopNotify } from "../features/mailbox/useDesktopNotify";
import { useAssistant } from "../features/assistant/useAssistant";
import { AssistantPanel } from "../features/assistant/AssistantPanel";
import { AssistantLauncher } from "../features/assistant/AssistantLauncher";
import { ToastProvider } from "../components/Toast";
import { useFocusTrap, useGlobalKeydown, useRestoreFocus } from "../lib/keyboard";
import type { CatalogHit, NotificationItem, TemplateSummary } from "../api/types";

// 應用外框:固定寬 264px 的側邊欄 + 頂列 + 內容區。
// <1024px 時側邊欄收合成抽屜,由頂列的選單鈕開啟。

/** 側邊欄寬度。設計稿定的 264px,只在這裡寫一次。 */
const SIDEBAR_WIDTH = "16.5rem";

/**
 * 通知面板那一塊的標記。
 *
 * 外框要判斷「焦點在不在面板裡」,而面板的 ref 在它自己身上。用一個講明白的
 * 屬性當兩者之間的約定,比拿 aria-label 的中文字去比對穩 —— 那句字是給人看的,
 * 改了不該讓鍵盤行為跟著壞掉。
 */
const NOTIFICATION_PANEL_MARK = "[data-nq-panel='notifications']";

/** 焦點在不在通知面板裡面。 */
function isInsideNotificationPanel(node: Element | null): boolean {
  return node instanceof HTMLElement && node.closest(NOTIFICATION_PANEL_MARK) !== null;
}

/** 焦點在不在**別的**浮層/對話框裡面(通知面板自己不算)。 */
function isInsideAnotherDialog(node: Element | null): boolean {
  if (!(node instanceof HTMLElement)) return false;
  if (isInsideNotificationPanel(node)) return false;
  return node.closest('[role="dialog"]') !== null;
}

export interface AppShellProps {
  children: ReactNode;
  /** 頂列的內容;外框會自行補上搜尋、選單鈕與通知鈴鐺。 */
  topBar?: Omit<TopBarProps, "onOpenSearch" | "onOpenMenu" | "bell">;
  /** 本單章節導覽。 */
  sections?: SectionNavItem[];
  onJumpSection?: (id: SectionNavId) => void;
  activeSectionId?: SectionNavId | null;
  /** 全域搜尋選到品項庫項目時,帶入目前章節。 */
  onPickCatalog?: (hit: CatalogHit) => void;
  /** 全域搜尋選到範本時套用。 */
  onApplyTemplate?: (template: TemplateSummary) => void;
  /** 本單明細行,供全域搜尋跳轉。 */
  lines?: CommandLineTarget[];
  onJumpLine?: (id: number | string) => void;
}

export function AppShell({
  children,
  topBar,
  sections = [],
  onJumpSection,
  activeSectionId = null,
  onPickCatalog,
  onApplyTemplate,
  lines,
  onJumpLine,
}: AppShellProps) {
  const navigate = useNavigate();
  // 新信的桌面通知(信箱 §2.6):本人在信箱頁開過、分頁在背景時才跳;點了開信箱頁
  useDesktopNotify(() => navigate("/mailbox"));
  const location = useLocation();
  const palette = useCommandPalette();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // 通知欄。狀態放在外框:鈴鐺在頂列、面板在右側,兩邊看的是同一份資料。
  const notifications = useNotifications();
  const {
    open: notificationsOpen,
    close: closeNotificationsState,
    markRead,
  } = notifications;
  const bellRef = useRef<HTMLButtonElement>(null);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // 關掉面板時,**只有焦點本來就在面板裡**才把它還給鈴鐺:面板是從那一顆按鈕
  // 開出來的,關掉之後把人丟回頁首,鍵盤使用者得重新 Tab 一輪才回得到原地。
  // 反過來說,滑鼠點遮罩收起面板時焦點根本不在裡面 —— 那時還去搶焦點,
  // 等於把使用者正在打字的地方換掉。
  const closeNotifications = useCallback(() => {
    const focusWasInside = isInsideNotificationPanel(
      typeof document === "undefined" ? null : document.activeElement,
    );
    closeNotificationsState();
    if (focusWasInside) bellRef.current?.focus();
  }, [closeNotificationsState]);

  // 面板自己沒有接 Esc,這條出路由外框接上(另外兩條是關閉鈕與再按一次鈴鐺)。
  // 但這條監聽掛在 window 上,**無條件攔下每一個 Esc 是不行的**:
  //
  // 1. 報價單列表上的三個浮層(漏斗、日期區間、顯示欄位)按 Esc 會自己收起來、
  //    `preventDefault()`,並把焦點還給觸發它的那顆按鈕。這裡再攔一次的話,
  //    window 階段一定晚於 body 階段,最後焦點會被搶到頂列的鈴鐺上 ——
  //    鍵盤使用者一收起浮層就被丟回頁首,正是 HeaderFilter 特地要擋的事。
  // 2. 全域搜尋(CommandPalette)的 Esc **沒有** preventDefault,所以只看
  //    `defaultPrevented` 還不夠;焦點落在別的 role="dialog" 裡時,那一下 Esc
  //    要的是關掉那一個,不是順手把通知面板也收掉。
  //
  // 兩條一起看:這一下 Esc 沒有人處理過,而且焦點不在別的浮層裡,才是要關面板。
  useGlobalKeydown((event) => {
    if (event.key !== "Escape") return;
    if (event.defaultPrevented) return;
    const active = typeof document === "undefined" ? null : document.activeElement;
    if (isInsideAnotherDialog(active)) return;
    closeNotifications();
  }, notificationsOpen);

  // 點一則:標成已讀,並跳到它指的地方。
  // **不順手把面板關掉** —— 連看三則會變成開三次;
  // 遮罩、關閉鈕與 Esc 三條路本來就都關得掉。
  const openNotification = useCallback(
    (item: NotificationItem) => {
      markRead(item);
      if (item.link) navigate(item.link);
    },
    [markRead, navigate],
  );

  // 抽屜蓋著整個頁面,所以無障礙的規矩與對話框相同:焦點困在裡面、
  // Esc 關得掉、關閉後焦點還給觸發鈕。少了這些,鍵盤使用者按下「開啟選單」
  // 之後往下 Tab,焦點會落到被遮罩蓋住的頁面內容上 —— 在遮罩底下等於看不見。
  useFocusTrap(drawerRef, drawerOpen);
  useRestoreFocus(drawerOpen);
  useGlobalKeydown((event) => {
    if (event.key === "Escape") closeDrawer();
  }, drawerOpen);

  // 開啟時把焦點移進面板,鍵盤使用者不必自己 Tab 進來(而且反向才進得去)。
  useEffect(() => {
    if (!drawerOpen) return;
    const panel = drawerRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus();
  }, [drawerOpen]);

  // 抽屜開著時鎖住背景捲動。
  useEffect(() => {
    if (!drawerOpen || typeof document === "undefined") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  // 換頁就收起抽屜,免得擋住剛打開的畫面。
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  return (
    <ToastProvider>
      <div className="flex min-h-screen bg-[var(--bg)]">
        {/* 桌機:固定側邊欄。 */}
        <aside
          className="sticky top-0 hidden h-screen shrink-0 md:block"
          style={{ width: SIDEBAR_WIDTH }}
          aria-label="側邊欄"
        >
          <Sidebar
            onOpenSearch={palette.openPalette}
            sections={sections}
            onJumpSection={onJumpSection}
            activeSectionId={activeSectionId}
          />
        </aside>

        {/* 手機與平板:抽屜。 */}
        {drawerOpen ? (
          <div className="fixed inset-0 z-40 md:hidden">
            {/* 遮罩不是唯一的關閉路徑,另有 Esc 與抽屜內的關閉鈕。 */}
            <div
              aria-hidden="true"
              data-testid="nq-drawer-backdrop"
              onClick={closeDrawer}
              className="absolute inset-0 bg-[rgba(28,25,23,0.42)]"
            />
            <div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label="側邊欄"
              tabIndex={-1}
              className="absolute inset-y-0 left-0 h-full max-w-[85vw]"
              style={{ width: SIDEBAR_WIDTH }}
            >
              <Sidebar
                drawer
                onClose={closeDrawer}
                onOpenSearch={() => {
                  closeDrawer();
                  palette.openPalette();
                }}
                sections={sections}
                onJumpSection={onJumpSection}
                activeSectionId={activeSectionId}
              />
            </div>
          </div>
        ) : null}

        {/* 通知面板是懸浮的,**不在這裡讓出任何寬度**(業主 2026-09-17:
            「不要壓縮版面 因為他不太可能會常駐」)。
            這裡原本有一個 `2xl:pr-[22rem]`,那就是把內容推開的那一行。 */}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar
            {...topBar}
            onOpenSearch={palette.openPalette}
            onOpenMenu={() => setDrawerOpen(true)}
            bell={
              <>
                {/* 亮色 / 暗色切換,就在鈴鐺旁邊(業主 2026-09-25)。 */}
                <ThemeToggle />
                <NotificationBell
                  ref={bellRef}
                  unreadCount={notifications.unreadCount}
                  open={notificationsOpen}
                  onToggle={notifications.toggle}
                />
                {/* 未讀數變動的即時播報(第 13.5 輪審查)。
                    徽章亮起來對看不到畫面的人等於什麼都沒發生:唯一會跟著變的是
                    鈴鐺按鈕的 aria-label,而**焦點不在那顆按鈕上時,aria-label 的
                    變動不會被播報** —— 一整輪把輪詢換成 SSE,他拿到的體驗仍然是
                    得自己去摸鈴鐺(或重新整理)才知道有沒有事。
                    這個區域要**常駐**:等到有話要說才把它掛上去的話,第一次的
                    變動會被漏掉(區域要先在無障礙樹上,之後寫進去的字才會被播報)。
                    內容由 useNotifications 給,它只在數字真的變了之後才有字 ——
                    掛載那一趟留空,不然每次換頁都會朗讀一次基準值。 */}
                <span
                  data-testid="nq-unread-live"
                  className="sr-only"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {notifications.announcement}
                </span>
              </>
            }
          />
          <main className="min-w-0 flex-1 px-3 py-4 sm:px-5">{children}</main>
        </div>
      </div>

      {/* 助理:右下角常駐的吉祥物 + 蓋上來的聊天視窗(業主 2026-09-17)。
          與通知面板同一種東西 —— 蓋在內容上,不擠壓版面。 */}
      {assistantEnabled() ? <AssistantCorner /> : null}

      <NotificationPanel
        open={notificationsOpen}
        items={notifications.items}
        unreadCount={notifications.unreadCount}
        loading={notifications.loading}
        failed={notifications.failed}
        onClose={closeNotifications}
        onSelect={openNotification}
        onMarkAllRead={notifications.markAllRead}
        onClearRead={notifications.clearRead}
        readCount={notifications.readCount}
      />

      <CommandPalette
        controller={palette}
        onNavigate={handleNavigate}
        onPickCatalog={onPickCatalog}
        onApplyTemplate={onApplyTemplate}
        lines={lines}
        onJumpLine={onJumpLine}
      />
    </ToastProvider>
  );
}

/**
 * 系統助理開不開。業主 2026-11-03:「107 助理功能關閉 右下角星星也關閉 DOCKER 也關閉」
 * —— 兩個月只有一位同仁問過 9 句。預設關閉;要開回來,建置時設 `VITE_ASSISTANT=on`,
 * 並用 `docker compose --profile assistant up -d llm` 把模型開起來。
 */
function assistantEnabled(): boolean {
  return import.meta.env.VITE_ASSISTANT === "on";
}

/**
 * 右下角的吉祥物與聊天視窗(第 21 輪)。獨立成一個元件,關掉的時候整個不掛載 ——
 * `useAssistant` 一掛載就會去問 `/assistant/health`,關了就不該再問。
 */
function AssistantCorner() {
  const assistant = useAssistant();
  return (
    <>
      <AssistantLauncher assistant={assistant} />
      <AssistantPanel assistant={assistant} />
    </>
  );
}
